import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getCorrections, createCorrection, getAccounts } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Lightbulb,
  ArrowRight,
  Lightning,
  Clock,
  Brain,
  Funnel
} from '@phosphor-icons/react';

export default function CorrectionsPage() {
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchCorrections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchCorrections = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { correction_type: filter } : {};
      const response = await getCorrections(params);
      setCorrections(response.data.corrections);
    } catch (error) {
      toast.error('Failed to load corrections');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Learning Hub">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const policyCorrections = corrections.filter(c => c.correction_type === 'policy_level');
  const oneOffCorrections = corrections.filter(c => c.correction_type === 'one_off');

  return (
    <Layout title="Learning Hub">
      <div data-testid="corrections-page" className="space-y-6">
        {/* Educational Banner */}
        <div className="bg-violet-50 border border-violet-200 rounded-sm p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-violet-100 border border-violet-200 flex items-center justify-center rounded-sm">
              <Lightbulb size={24} weight="duotone" className="text-violet-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight mb-2">A correction is not always a new pattern</h2>
              <p className="text-sm text-violet-900 leading-relaxed mb-4">
                When you correct the AI, we capture <strong>what changed</strong>, <strong>why it changed</strong>, and most importantly, 
                <strong> whether it's a one-off fix or a policy-level change</strong>. Policy-level corrections automatically generate 
                new context rules to improve future classifications.
              </p>
              <div className="flex gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={18} className="text-amber-500" />
                  <span className="text-violet-700"><strong>One-off:</strong> This specific transaction was unusual</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lightning size={18} className="text-emerald-500" />
                  <span className="text-violet-700"><strong>Policy:</strong> All similar transactions should be handled this way</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Total Corrections</p>
            <p className="text-3xl font-bold font-mono">{corrections.length}</p>
          </div>
          <div className="bg-white border border-emerald-200 rounded-sm p-6">
            <div className="flex items-center gap-2 mb-2">
              <Lightning size={16} className="text-emerald-500" />
              <p className="text-xs uppercase tracking-widest text-emerald-600">Policy-Level</p>
            </div>
            <p className="text-3xl font-bold font-mono text-emerald-600">{policyCorrections.length}</p>
            <p className="text-xs text-zinc-500 mt-1">Created {policyCorrections.filter(c => c.generated_rule_id).length} rules</p>
          </div>
          <div className="bg-white border border-amber-200 rounded-sm p-6">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-amber-500" />
              <p className="text-xs uppercase tracking-widest text-amber-600">One-Off Fixes</p>
            </div>
            <p className="text-3xl font-bold font-mono text-amber-600">{oneOffCorrections.length}</p>
            <p className="text-xs text-zinc-500 mt-1">Exception cases only</p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 rounded-sm" data-testid="correction-type-filter">
              <SelectValue placeholder="All Corrections" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Corrections</SelectItem>
              <SelectItem value="policy_level">Policy-Level Only</SelectItem>
              <SelectItem value="one_off">One-Off Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Corrections List */}
        {corrections.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center">
            <Lightbulb size={48} className="mx-auto mb-4 text-zinc-300" />
            <p className="font-medium mb-2">No corrections recorded yet</p>
            <p className="text-sm text-zinc-500">
              Corrections are captured when you override AI classifications in the Review Queue
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {corrections.map((correction) => (
              <div
                key={correction.correction_id}
                data-testid={`correction-card-${correction.correction_id}`}
                className="bg-white border border-zinc-200 rounded-sm overflow-hidden"
              >
                <div className="px-6 py-4 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {correction.correction_type === 'policy_level' ? (
                      <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-sm">
                        <Lightning size={16} className="text-emerald-600" />
                        <span className="text-xs uppercase tracking-widest text-emerald-700">Policy-Level</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-sm">
                        <Clock size={16} className="text-amber-600" />
                        <span className="text-xs uppercase tracking-widest text-amber-700">One-Off</span>
                      </div>
                    )}
                    <span className="text-sm text-zinc-500">
                      {new Date(correction.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  {correction.generated_rule_id && (
                    <div className="flex items-center gap-2 text-xs text-violet-600">
                      <Funnel size={14} />
                      <span>Rule auto-generated</span>
                    </div>
                  )}
                </div>

                <div className="p-6">
                  {/* Change Visualization */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex-1 bg-red-50 border border-red-200 rounded-sm p-4">
                      <p className="text-xs uppercase tracking-widest text-red-500 mb-2">Original (AI)</p>
                      <div className="flex items-center gap-2">
                        <Brain size={16} className="text-red-400" />
                        <span className="text-sm font-mono">
                          {correction.original_classification?.account_id || correction.original_classification?.suggested_account || 'No classification'}
                        </span>
                      </div>
                    </div>
                    <ArrowRight size={24} className="text-zinc-400 flex-shrink-0" />
                    <div className="flex-1 bg-emerald-50 border border-emerald-200 rounded-sm p-4">
                      <p className="text-xs uppercase tracking-widest text-emerald-500 mb-2">Corrected</p>
                      <span className="text-sm font-mono font-medium text-emerald-700">
                        {correction.corrected_classification?.account_id || 'Manual assignment'}
                      </span>
                    </div>
                  </div>

                  {/* Explanation */}
                  <div>
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Explanation</p>
                    <p className="text-sm text-zinc-700 leading-relaxed bg-zinc-50 p-4 rounded-sm border border-zinc-100">
                      {correction.explanation || 'No explanation provided'}
                    </p>
                  </div>

                  {/* Transaction Reference */}
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <p className="text-xs text-zinc-400">
                      Transaction ID: <span className="font-mono">{correction.transaction_id}</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
